// Consolidated traceability snapshot for a finalised OT. Powers the
// "view traceability data" dialog on the Terminer panel and feeds the
// xlsx + ZIP export builder. Pure derivation — every field comes from
// the OT detail payload, no extra fetches.

import type { OT, OTLotLink } from "@/features/transit/api/types"
import { latestDraftBL, lotsTotalBags, lotsTotalKg } from "@/features/transit/lib/ot-math"

export interface TraceabilityLotEntry {
  lotId: number
  lotNumber: string
  pid?: string | null
  bags: number
  netKg: number
  netMT: number
  usinageNumber?: string | null
  cccKind?: "BV" | "BA" | null
  cccBvNumber?: string | null
  cccIssuedAt?: string | null
}

export interface TraceabilityDocCounts {
  slotDocs: number
  draftBLs: number
  photos: number
  empotageFiles: number
  redevances: number
  cccDocs: number
}

export interface TraceabilitySnapshot {
  generatedAt: string
  ot: {
    id: number
    otNumber: string
    label: string
    stage: OT["stage"]
    archived: boolean
    completedAt?: string | null
    clientName: string
    contractId: number
    contractNumber?: string | null
    certifications: string[]
  }
  shipment: {
    freightForwarder?: string | null
    bookingRef?: string | null
    vessel?: string | null
    voyage?: string | null
    pol?: string | null
    pod?: string | null
    etd?: string | null
    saigicNumber?: string | null
    fumigationAllowed: boolean
  }
  totals: {
    lotCount: number
    totalBags: number
    totalNetKg: number
    totalNetMT: number
    totalStuffedWeightKg?: number | null
    finalBLWeightKg?: number | null
  }
  lots: TraceabilityLotEntry[]
  documents: TraceabilityDocCounts
  share?: OT["traceabilityShared"] | null
}

export function buildTraceabilitySnapshot(ot: OT): TraceabilitySnapshot {
  const lots: TraceabilityLotEntry[] = (ot.lots ?? []).map(toLotEntry)

  const totalNetKg = lotsTotalKg(ot)
  const latestBL = latestDraftBL(ot)

  return {
    generatedAt: new Date().toISOString(),
    ot: {
      id: ot.id,
      otNumber: ot.otNumber,
      label: ot.label,
      stage: ot.stage,
      archived: ot.archived,
      completedAt: ot.completedAt ?? null,
      clientName: ot.clientName,
      contractId: ot.contractId,
      contractNumber: ot.contractNumber ?? null,
      certifications: ot.certifications ?? [],
    },
    shipment: {
      freightForwarder: ot.freightForwarder ?? null,
      bookingRef: ot.bookingRef ?? null,
      vessel: ot.vessel ?? null,
      voyage: ot.voyage ?? null,
      pol: ot.pol ?? null,
      pod: ot.pod ?? null,
      etd: ot.etd ?? null,
      saigicNumber: ot.saigicNumber ?? null,
      fumigationAllowed: ot.fumigationAllowed,
    },
    totals: {
      lotCount: lots.length,
      totalBags: lotsTotalBags(ot),
      totalNetKg,
      totalNetMT: totalNetKg / 1000,
      totalStuffedWeightKg: ot.totalStuffedWeightKg ?? null,
      finalBLWeightKg: latestBL?.weightKg ?? null,
    },
    lots,
    documents: docCounts(ot),
    share: ot.traceabilityShared ?? null,
  }
}

function toLotEntry(link: OTLotLink): TraceabilityLotEntry {
  const lot = link.lot
  const netKg = lot?.netKg ?? 0
  return {
    lotId: link.lotId,
    lotNumber: lot?.lotNumber ?? `#${link.lotId}`,
    pid: lot?.pid ?? null,
    bags: lot?.bags ?? 0,
    netKg,
    netMT: netKg / 1000,
    usinageNumber: lot?.usinage?.usinageNumber ?? null,
    cccKind: link.cccKind ?? null,
    cccBvNumber: link.cccBvNumber ?? null,
    cccIssuedAt: link.cccIssuedAt ?? null,
  }
}

function docCounts(ot: OT): TraceabilityDocCounts {
  const cccDocs = (ot.lots ?? []).reduce(
    (acc, link) => acc + (link.cccDocs?.length ?? 0),
    0,
  )
  return {
    slotDocs: ot.docs?.length ?? 0,
    draftBLs: ot.draftBLs?.length ?? 0,
    photos: ot.photos?.length ?? 0,
    empotageFiles: ot.empotageFiles?.length ?? 0,
    redevances: ot.redevances?.length ?? 0,
    cccDocs,
  }
}

